import { ButtonVariantOption, ButtonColorOption } from "@/components/Button";
import { StyledDataGrid } from "@/components/DataGrid/ChibanKaokuNumberSearchStyleDataGrid";
import { type GeoSpaceChiban, type PropertySelectionRow } from "../types";
import {
  type GridColDef,
  type GridSelectionModel,
  type GridRenderCellParams,
  type GridColumnHeaderParams,
} from "@mui/x-data-grid-pro";
import { useCallback, useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button as MuiButton,
  Chip,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { ResponsiveTable } from "@/components/Responsive";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MIN_TAP_TARGET } from "@/styles/responsive";
import {
  convertFullWidthDigitsAndHyphenToHalfWidth,
  convertTypeEnglishToJapanese,
  formatArea,
  skipSelectChiban,
  normalizeLocation,
  normalizeChibanKaokuNumber,
} from "../utils";
import { type BookTypeEng } from "@/types/acquirebook";
import { formatNumberWithComma } from "@/utils/formatNumber";

/** 1ページに出す件数（PC の DataGrid と SP のカードで共通） */
const RANGE_SELECT_RESULT_PAGE_SIZE = 50;

/**
 * SP 用の一覧。1 行 = 1 カードで出し、50 件ずつページで切り替える。
 * 並び順は PC の DataGrid の初期ソート（id の降順）に合わせる。
 */
const RangeSelectResultCards: React.FC<{
  rows: PropertySelectionRow[];
  page: number;
  setPage: (page: number) => void;
  onDelete: (id: number) => void;
}> = ({ rows, page, setPage, onDelete }) => {
  const sortedRows = [...rows].sort((a, b) => b.id - a.id);
  const pageRows = sortedRows.slice(
    page * RANGE_SELECT_RESULT_PAGE_SIZE,
    (page + 1) * RANGE_SELECT_RESULT_PAGE_SIZE
  );
  const pageCount = Math.ceil(rows.length / RANGE_SELECT_RESULT_PAGE_SIZE);

  const tableRows = pageRows.map((row) => [
    convertTypeEnglishToJapanese(String(row.bookType)),
    row.prefName,
    row.location,
    convertFullWidthDigitsAndHyphenToHalfWidth(row.chibanKaokuNumber),
    formatArea(row.area as number),
    <MuiButton
      key="delete"
      size={SizingWrapperStyle.SMALL}
      variant={ButtonVariantOption.Outlined}
      color={ButtonColorOption.Error}
      onClick={() => {
        onDelete(row.id);
      }}
      sx={{ minHeight: MIN_TAP_TARGET }}
    >
      削除する
    </MuiButton>,
  ]);

  return (
    <Stack spacing={1.5}>
      <ResponsiveTable
        title="範囲選択で取得した物件"
        headers={[
          "種別",
          "都道府県",
          "所在",
          "地番または家屋番号",
          "推計地積(㎡)",
          "操作",
        ]}
        rows={tableRows}
        emptyMessage="データがありません。"
      />
      {pageCount > 1 && (
        <Pagination
          count={pageCount}
          // Pagination は 1 始まり、DataGrid の page は 0 始まり
          page={page + 1}
          onChange={(_, nextPage) => {
            setPage(nextPage - 1);
          }}
          sx={{ alignSelf: "center" }}
        />
      )}
    </Stack>
  );
};

interface IProps {
  rows: PropertySelectionRow[];
  setRows: React.Dispatch<React.SetStateAction<PropertySelectionRow[]>>;
  setSelectedProperties: React.Dispatch<
    React.SetStateAction<PropertySelectionRow[]>
  >;
  setRangeSelectModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  buildLocationString: (r: GeoSpaceChiban) => string;
  findDuplicatePropertyIndex: (
    rows: PropertySelectionRow[],
    bookType: BookTypeEng,
    prefName: string,
    location: string,
    chiban: string
  ) => number;
  mergePropertySelectionRow: (
    rows: PropertySelectionRow[],
    index: number,
    area: number | null,
    rawData?: GeoSpaceChiban
  ) => PropertySelectionRow[] | undefined;
  geospaceReverseByShapeResult: GeoSpaceChiban[] | undefined;
  /**
   * 検索に使ったデータソースの取得件数の上限。指定があれば上限の説明を出す。
   *
   * 上限はソースごとに異なる（`RANGE_SEARCH_RESULT_LIMIT`）。ブルーマップには無い。
   */
  resultLimit?: number;
  /**
   * 該当件数が上限を超え、結果が打ち切られたか。
   *
   * 打ち切りを BE が返すのは登記所備付地図だけ。立っていれば範囲を狭めるよう知らせる。
   */
  truncated?: boolean;
  /**
   * 検索に使ったデータソース名（例:「役所地番地図」）。
   *
   * 範囲検索は 1 ソースだけを引くため、行ごとではなく一覧全体の出典として表示する。
   */
  sourceLabel?: string;
  /**
   * 非表示のソース（Tier 2）で引いたときに、該当レイヤーを表示する処理。
   *
   * 表示中のソースで引けた場合は乖離が無いため渡されない（＝ボタンを出さない）。
   */
  onShowSourceLayer?: () => void;
}

export const RangeSelectResultModal: React.FC<IProps> = (props) => {
  const {
    rows,
    setRows,
    setSelectedProperties,
    setRangeSelectModalIsOpen,
    findDuplicatePropertyIndex,
    mergePropertySelectionRow,
    buildLocationString,
    geospaceReverseByShapeResult,
    resultLimit,
    truncated = false,
    sourceLabel,
    onShowSourceLayer,
  } = props;

  const isMobile = useIsMobile();

  // 表示中のページ（0 始まり）。PC の DataGrid と SP のカードで共有する
  const [page, setPage] = useState(0);
  // 削除で件数が減ってページが無くなったら、最後のページに寄せる
  const lastPage = Math.max(
    0,
    Math.ceil(rows.length / RANGE_SELECT_RESULT_PAGE_SIZE) - 1
  );
  const currentPage = Math.min(page, lastPage);

  const [selectedRowIds, setSelectedRowIds] = useState<GridSelectionModel>(() =>
    rows.filter((row) => row.selected).map((row) => row.id)
  );

  useEffect(() => {
    setSelectedRowIds(rows.filter((row) => row.selected).map((row) => row.id));
  }, [rows]);

  const handleRowSelectionChange = useCallback(
    (newSelectionModel: GridSelectionModel) => {
      setSelectedRowIds(newSelectionModel);
      setRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          selected: newSelectionModel.includes(row.id),
        }))
      );
    },
    [setRows]
  );

  const [deletedRows, setDeletedRows] = useState<PropertySelectionRow[]>([]);

  const handleDelete = useCallback(
    (id: number) => {
      setRows((prevRows) => {
        // 削除する前に、削除対象の行を取得
        const rowToDelete = prevRows.find((row) => row.id === id);

        // 削除対象の行を配列に追加
        if (rowToDelete) {
          setDeletedRows((prevDeletedRows) => [
            ...prevDeletedRows,
            rowToDelete,
          ]);
        }

        // 行を削除して新しい配列を返す
        return prevRows.filter((row) => row.id !== id);
      });
    },
    [setRows]
  );

  // カラム設定
  const columns: GridColDef[] = [
    {
      field: "id",
      align: "center",
    },
    {
      field: "bookType",
      headerName: "種別",
      sortable: false,
      align: "center",
      flex: 0.5,
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        return convertTypeEnglishToJapanese(String(value));
      },
    },
    {
      field: "prefName",
      headerName: "都道府県",
      sortable: false,
      align: "center",
      flex: 1,
      headerAlign: "center",
    },
    {
      field: "location",
      headerName: "所在",
      sortable: false,
      flex: 2,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        // 長い文字列の場合は折り返す
        return <Box sx={{ whiteSpace: "normal" }}>{value}</Box>;
      },
    },
    {
      field: "chibanKaokuNumber",
      headerName: "地番または家屋番号",
      sortable: false,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        return convertFullWidthDigitsAndHyphenToHalfWidth(value as string);
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <strong>
          地番または
          <br />
          家屋番号
        </strong>
      ),
    },
    {
      field: "area",
      headerName: "推計地積(㎡)",
      sortable: false,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams) => {
        return formatArea(value as number);
      },
      renderHeader: (params: GridColumnHeaderParams) => (
        <strong>
          推計地積
          <br />
          (㎡)
        </strong>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 80,
      sortable: false,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <Box>
            <MuiButton
              size={SizingWrapperStyle.SMALL}
              variant={ButtonVariantOption.Outlined}
              color={ButtonColorOption.Error}
              onClick={() => {
                handleDelete(params.id as number);
              }}
            >
              削除する
            </MuiButton>
          </Box>
        );
      },
    },
  ];

  // 物件リストに追加をクリックした時の処理
  const handleOkClick = (): void => {
    if (Array.isArray(geospaceReverseByShapeResult)) {
      geospaceReverseByShapeResult.forEach((result: GeoSpaceChiban) => {
        // 日本語の文字またはアルファベットを含む場合はスキップ（CHIBAN_FULL_PATTERNは除く）
        if (skipSelectChiban(result.chiban)) {
          return;
        }

        const location = normalizeLocation(buildLocationString(result));
        const chiban = normalizeChibanKaokuNumber(result.chiban);

        // 登記取得物件選択テーブルに追加
        setSelectedProperties((prev) => {
          const bookType = "LAND" as BookTypeEng;
          const duplicateIndex = findDuplicatePropertyIndex(
            prev,
            bookType,
            result.prefName,
            location,
            chiban
          );

          if (duplicateIndex !== -1) {
            const merged = mergePropertySelectionRow(
              prev,
              duplicateIndex,
              result.area,
              result
            );
            if (merged) {
              return merged;
            }

            return prev;
          }

          // モーダルで削除された行と全てのプロパティが一致する場合は追加しない
          if (
            deletedRows.some(
              (deletedRow) =>
                deletedRow.bookType === bookType &&
                deletedRow.prefName === result.prefName &&
                normalizeLocation(deletedRow.location) === location &&
                normalizeChibanKaokuNumber(deletedRow.chibanKaokuNumber) ===
                  chiban &&
                deletedRow.area === result.area &&
                JSON.stringify(deletedRow.rawData) === JSON.stringify(result)
            )
          ) {
            return prev;
          }

          const lastId: number = prev.length > 0 ? prev[prev.length - 1].id : 0;
          return [
            ...prev,
            {
              id: lastId + 1,
              selected: true,
              bookType,
              prefName: result.prefName,
              location,
              chibanKaokuNumber: chiban,
              area: result.area,
              isFeedOrigin: false,
              rawData: result,
            },
          ].sort((a, b) => a.id - b.id);
        });
      });

      // テーブルを初期化
      setRows([]);
      setRangeSelectModalIsOpen(false);
    }
  };

  // キャンセルをクリックした時の処理
  const handleChancelClick = (): void => {
    // テーブルを初期化
    setRows([]);
    setRangeSelectModalIsOpen(false);
  };

  // rowsの個数を計算
  const rowCount = rows.length;

  return (
    <Box
      sx={{
        height: 500,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        p: 2,
        bgcolor: "background.paper",
        boxShadow: 3,
        borderRadius: 2,
        // SP は画面いっぱいに出す（外枠の Modal 側も全画面にしている）
        ...(isMobile && {
          height: "100%",
          boxSizing: "border-box",
          boxShadow: "none",
          borderRadius: 0,
        }),
      }}
    >
      <Typography
        variant={isMobile ? "subtitle1" : "h6"}
        sx={{ mb: 2, fontWeight: isMobile ? 700 : undefined }}
      >
        {rowCount > 0
          ? `${formatNumberWithComma(rowCount)} 件のデータ取得が完了しました。`
          : "データがありません。"}
      </Typography>

      {truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {resultLimit !== undefined
            ? `表示件数が上限（${formatNumberWithComma(resultLimit)}件）`
            : "表示件数が上限"}
          に達したため、一部のみ表示しています。範囲を狭めて再度検索してください。
        </Alert>
      )}

      {isMobile ? (
        // SP は表をカードにし、一覧の部分だけをスクロールさせる（フッターのボタンは常に見せる）
        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: "auto", mb: 2 }}>
          <RangeSelectResultCards
            rows={rows}
            page={currentPage}
            setPage={setPage}
            onDelete={handleDelete}
          />
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1, mb: 2 }}>
          <StyledDataGrid
            disableColumnMenu
            disableSelectionOnClick={true}
            rows={rows}
            pageSize={RANGE_SELECT_RESULT_PAGE_SIZE}
            page={currentPage}
            onPageChange={setPage}
            columns={columns}
            initialState={{
              sorting: {
                sortModel: [{ field: "id", sort: "desc" }],
              },
              columns: {
                columnVisibilityModel: {
                  id: false,
                },
              },
            }}
            onSelectionModelChange={handleRowSelectionChange}
          />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-between",
          gap: 2,
          // SP は案内文を横いっぱいに出し、ボタンはその下に右寄せで並べる
          ...(isMobile && { flexDirection: "column", gap: 1.5 }),
        }}
      >
        <Alert
          severity="info"
          sx={{
            flex: "1 1 auto",
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            },
          }}
        >
          {sourceLabel && (
            <Chip
              label={sourceLabel}
              size={"small"}
              variant={"outlined"}
              sx={{ height: 20, "& .MuiChip-label": { fontSize: "0.7rem" } }}
            />
          )}
          <span>
            {resultLimit !== undefined &&
              `一度の取得件数は最大${formatNumberWithComma(
                resultLimit
              )}件です。`}
            道や水域などは取り除かれます。
          </span>
          {onShowSourceLayer && sourceLabel && (
            <MuiButton
              size={SizingWrapperStyle.SMALL}
              variant={ButtonVariantOption.Outlined}
              onClick={onShowSourceLayer}
              sx={{ textTransform: "none" }}
            >
              {sourceLabel}を表示
            </MuiButton>
          )}
        </Alert>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            ...(isMobile && {
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 1,
            }),
          }}
        >
          <MuiButton
            onClick={handleChancelClick}
            sx={{
              textTransform: "none",
              ...(isMobile && { minHeight: MIN_TAP_TARGET }),
            }}
            variant="outlined"
          >
            キャンセル
          </MuiButton>
          <MuiButton
            onClick={handleOkClick}
            sx={{
              textTransform: "none",
              ...(isMobile && { minHeight: MIN_TAP_TARGET }),
            }}
            variant="contained"
          >
            物件リストに追加
          </MuiButton>
        </Box>
      </Box>
    </Box>
  );
};
